// EXECUTOR-WRITE-1 Phase 4R (s144, D-144) — executor plan-view inline-editing support.
//
// The executor per-category plan view IS the shared GenericCategoryPage. Per D-144 the executor's
// page works like the planner's page: the SAME form fields, edited in place with the SAME widgets —
// no separate "corrections" panel. This module carries the executor-only layer (gate + overlay
// read + save-routing helper) mounted ON TOP, WITHOUT touching the planner/advisor render:
// everything gates on `active` (executor + readOnly + verified death), byte-identical when false.
//
// The plan view renders planner data via the shared /api/generic-categories read path (kept
// byte-identical). This module fetches the executor-only OVERLAY (/api/executor/:clientId/plan/
// section/:slug) — field provenance ("was …") + which rows the executor soft-deleted or added.
import { useQuery, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDeathEvent } from "./use-death-event";

export interface ExecutorPlanOverlay {
  /** `${rowId}::${fieldId}` → the planner's ORIGINAL value (before the first executor edit). */
  provenance: Record<string, string | null>;
  deletedRowIds: string[];
  addedRowIds: string[];
}

const EMPTY_OVERLAY: ExecutorPlanOverlay = { provenance: {}, deletedRowIds: [], addedRowIds: [] };

/** True once this is the executor's post-death settlement view (readOnly executor + verified
 *  death). Shares the death-event query key with ExecutorDashboard, so no extra request. */
export function useExecutorEditActive(clientId: string | undefined, enabled: boolean): boolean {
  const { data: deathEvent } = useDeathEvent(clientId, { enabled });
  return enabled && deathEvent?.status === "verified";
}

const overlayQueryKey = (clientId: string | undefined, slug: string) =>
  [`/api/executor/${clientId}/plan/section/${slug}`];

/** Fetch one section's plan-view overlay. React Query dedupes by key. Returns empty until loaded. */
export function useExecutorPlanOverlay(
  clientId: string | undefined,
  sectionSlug: string | undefined,
  enabled: boolean,
): ExecutorPlanOverlay {
  const { data } = useQuery<ExecutorPlanOverlay>({
    queryKey: overlayQueryKey(clientId, sectionSlug ?? ""),
    enabled: enabled && !!clientId && !!sectionSlug,
  });
  return data ?? EMPTY_OVERLAY;
}

/** Fetch overlays for MANY sections at once (one query per slug, react-query-deduped), returning a
 *  lookup by slug. Lets GenericCategoryPage read per-section overlay inside its render map without
 *  calling a hook per section (hooks-in-loop is illegal). Same query keys as the singular hook, so a
 *  mutation invalidating `/plan/section/:slug` refreshes both. */
export function useExecutorPlanOverlays(
  clientId: string | undefined,
  sectionSlugs: string[],
  enabled: boolean,
): (slug: string) => ExecutorPlanOverlay {
  const results = useQueries({
    queries: sectionSlugs.map((slug) => ({
      queryKey: overlayQueryKey(clientId, slug),
      enabled: enabled && !!clientId,
    })),
  });
  const bySlug = new Map<string, ExecutorPlanOverlay>();
  sectionSlugs.forEach((slug, i) => {
    bySlug.set(slug, (results[i]?.data as ExecutorPlanOverlay | undefined) ?? EMPTY_OVERLAY);
  });
  return (slug: string) => bySlug.get(slug) ?? EMPTY_OVERLAY;
}

/** The SHARED executor-deletion state for BOTH surfaces (Phase 4R, D-144): a set of currently-removed
 *  rowIds from the owner-authorized `/api/clients/:clientId/executor-removed-rows` read. The executor
 *  list dims + offers Undo; the planner render dims + reads "removed by the executor" — same predicate,
 *  same source. Enabled whenever the client is loaded (both surfaces); react-query dedupes by client. */
export function useExecutorRemovedRows(clientId: string | undefined, enabled: boolean): Set<string> {
  const { data } = useQuery<{ deletedRowIds: string[] }>({
    queryKey: ["/api/clients", clientId, "executor-removed-rows"],
    enabled: enabled && !!clientId,
  });
  // 🔴 s185: THE ONE TRANSLATION POINT between the executor wire vocabulary and the completion
  // vocabulary. The endpoint ships `{ deletedRowIds }` — half of the symmetric
  // `deletedRowIds`/`addedRowIds` pair the ExecutorPlanOverlay uses across two endpoints. Every
  // completion-side caller wants a `removedRowIds` Set. Unwrapping happens HERE and nowhere else:
  // a second hand-rolled fetch is what broke the dashboard in s185 (`new Set(responseObject)` →
  // "object is not iterable" → error boundary), and the fix is to have one place that knows the
  // wire shape, not to rename the wire.
  //
  // Memoised so the identity is stable across renders — callers put this straight into `useMemo`
  // dependency arrays, and a fresh Set every render would defeat them.
  return useMemo(() => new Set(data?.deletedRowIds ?? []), [data]);
}

/** Provenance/action key matching the server's `${rowId}::${fieldId}` (card rows use "__card__"). */
export function planRowKey(rowId: string | null | undefined): string {
  return rowId ?? "__card__";
}
export function provenanceKey(rowId: string | null | undefined, fieldId: string): string {
  return `${planRowKey(rowId)}::${fieldId}`;
}

/** The planner's original value for a field the executor overwrote, or undefined if never edited.
 *  Drives the inline "edited · was …" badge. */
export function fieldEditOf(
  overlay: ExecutorPlanOverlay,
  rowId: string | null | undefined,
  fieldId: string,
): { originalValue: string | null } | undefined {
  const key = provenanceKey(rowId, fieldId);
  if (!Object.prototype.hasOwnProperty.call(overlay.provenance, key)) return undefined;
  return { originalValue: overlay.provenance[key] };
}

/**
 * The fieldIds on `rowId` that carry an executor edit, across the FULL field universe of the
 * record (not just the ribbon/basic subset). Pure — reads the same `getFieldEdit` accessor the
 * cells already use (G7: one provenance source), so the collapsed-row summary count and the
 * per-field drawer markers can never disagree. `getFieldEdit` returns provenance only for edited
 * fields, so passing every field of the section is safe (unedited/inactive fields contribute
 * nothing). Order-preserving; deduped by the caller's field list.
 */
export function editedFieldIdsOnRow(
  fieldIds: string[],
  rowId: string,
  getFieldEdit: (fieldId: string, rowId: string) => { originalValue: string | null } | undefined,
): string[] {
  return fieldIds.filter((id) => getFieldEdit(id, rowId) !== undefined);
}
